import { EContractStatus, EFinancingProgram } from '@if/core/types'
import { isComputeIfXpressDtoValid } from '@src/data/worksheet-selectors'
import { ComputeFundingDto } from '@src/types/ComputeIfXpressFundingDto'
import { ComputeFundingResultDto } from '@src/types/ComputeIfXpressFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { EditIfXpressWorksheetDto } from '@src/types/WorksheetSchema'
import {
  keepPreviousData,
  MutationFunction,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { getApiClient } from '../api-client'

const SCOPE = 'worksheet'
const CREDIT_APP_SCOPE = 'credit-applications'
const SCOPEPERSOFUNDING = 'ixpressFunding'
const DETAIL = 'detail'
const keysFactory = {
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  ifxpressFunding: (id: string, dto: ComputeFundingDto) => [{ scope: SCOPEPERSOFUNDING, id, ...dto }] as const,

  creditAppDetail: (id: string, financingProgramId = EFinancingProgram.IfXpress) =>
    [{ scope: CREDIT_APP_SCOPE, entity: DETAIL, id, financingProgramId }] as const,
}

const GetIfXpressFunding = async ({
  queryKey: [{ id, ...dto }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['ifxpressFunding']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<ComputeFundingResultDto>(
    `/Worksheet/${EFinancingProgram.IfXpress}/${encodeURIComponent(id)}/ComputeFunding`,
    dto,
  )
  return response.data
}

export function useGetIfXpressFunding(id: string, dto: ComputeFundingDto): [ComputeFundingResultDto, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.ifxpressFunding(id, dto),
    queryFn: GetIfXpressFunding,
    enabled: isComputeIfXpressDtoValid(dto),
    placeholderData: keepPreviousData,
  })

  return [data!, isFetching]
}

const postIfXpressWorksheet: MutationFunction<FilteredWorksheet, { worksheet: EditIfXpressWorksheetDto }> = async ({
  worksheet,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<FilteredWorksheet>(
    `/Worksheet/${EFinancingProgram.IfXpress}/${encodeURIComponent(worksheet.creditApplicationId!)}`,
    worksheet,
  )

  return response.data
}

export function usePostIfXpressWorksheet(): [
  MutationFunction<FilteredWorksheet, { worksheet: EditIfXpressWorksheetDto }>,
  boolean,
  boolean,
  AxiosError<{ message: string }> | null,
  () => void,
] {
  const queryClient = useQueryClient()
  const { isPending, isError, mutateAsync, error, reset } = useMutation({
    mutationFn: postIfXpressWorksheet,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.creditAppDetail(data.creditApplicationId), (oldData: CreditApplication) => {
        return {
          ...oldData,
          worksheet: data,
          updatedOn: data.updatedOn,
          contract: { ...oldData.contract, status: EContractStatus.NotReady },
        }
      })
    },
  })

  return [mutateAsync, isPending, isError, error as AxiosError<{ message: string }>, reset]
}

const putIfXpressWorksheet: MutationFunction<FilteredWorksheet, { worksheet: EditIfXpressWorksheetDto }> = async ({
  worksheet,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<FilteredWorksheet>(
    `/Worksheet/${EFinancingProgram.IfXpress}/${encodeURIComponent(worksheet.creditApplicationId!)}`,
    worksheet,
  )
  return response.data
}

export function usePutIfXpressWorksheet(): [
  MutationFunction<FilteredWorksheet, { worksheet: EditIfXpressWorksheetDto }>,
  boolean,
  boolean,
  AxiosError<{ message: string }> | null,
  () => void,
] {
  const queryClient = useQueryClient()
  const { isPending, isError, error, mutateAsync, reset } = useMutation({
    mutationFn: putIfXpressWorksheet,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.creditAppDetail(data.creditApplicationId), (oldData: CreditApplication) => {
        return {
          ...oldData,
          worksheet: data,
          updatedOn: data.updatedOn,
          contract: { ...oldData.contract, status: EContractStatus.NotReady },
        }
      })
    },
  })

  return [mutateAsync, isPending, isError, error as AxiosError<{ message: string }>, reset]
}
